import {
  customElement,
  FASTElement,
  attr,
  observable,
  Observable,
  Splice,
} from '@microsoft/fast-element';
import {
  SELLER_DASHBOARD_GRID_TABS,
  VppaManagementTemplate as template,
} from './vppa-management.template';
import { VppaManagementStyles as styles } from './vppa-management.styles';
import { Project } from '../../../../types';
import { AllVppas } from '../all-vppas/all-vppas';
import { CONTRACT_TYPE, NEGOTIATION_STATUS, VPPA_RFQ_STATUS } from '../../../../utils';
import { Tabs } from '@genesislcap/foundation-zero';
import { ICompanyService } from '../../../../services/company.service';
import { NegotiationsGrid } from '../../../common/negotiations-grid/negotiations-grid';
import { COLUMN_FIELDS } from '../../../common/negotiations-grid/column-config'
import { hasLegalOrAuthorizedSignatoryVisibility } from '../../../../utils/profiles';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';

import {
  convertDateToUTC,
  getFormattedDate,
  getFormattedNumber,
  getNumericPattern,
} from '../../../../utils/formatting';
import { getGroupColumnContractType } from '../../../../utils/grid';
import { INotificationService } from '../../../../services/notification.service';
import { IUserService } from '../../../../services/user.service';

const name = 'vppa-management';

@customElement({
  name,
  template,
  styles,
})
export class VppaManagement extends FASTElement {
  @ICompanyService companyService: ICompanyService;
  @INotificationService notificationService: INotificationService;
  @IUserService userService: IUserService;

  @attr project: Project;
  @attr hasProject: Boolean;
  @attr projectsListEmpty: boolean;
  @observable projectId: string;
  @observable allVppa: AllVppas;
  @observable vppaGridTabs: Tabs;
  @observable companyId: string;
  @observable isNegotiationTabSelected: boolean = false;
  @observable negotiationGrid: NegotiationsGrid;
  @observable showNoRecentActivity: boolean = true;
  @attr @observable activeTabId: string;

  private notifier: any;
  private handler: any;

  connectedCallback() {
    super.connectedCallback();

    if (this.showOnlyNegotiations()) {
      this.vppaGridTabs.activeid = SELLER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB;
      this.isNegotiationTabSelected = true;
    }

    //Check if there is an existing tab coming from notification service
    const valueFromNotification = this.notificationService.getDashboardTab();
    if (valueFromNotification) {
      setTimeout(() => {
        this.vppaGridTabs.activeid = valueFromNotification;
        if (valueFromNotification === SELLER_DASHBOARD_GRID_TABS.TERMINATED_TAB) {
          this.allTerminatedVppa();
          this.notificationService.clearDashboardTab();
        }
      }, 500);
    }

    this.createNotificationNotifier();
  }

  private createNotificationNotifier() {
    const context = this;
    this.notifier = Observable.getNotifier(this.notificationService);
    this.handler = {
      handleChange(source: any, splices: Splice[]) {
        const valueFromNotification = context.notificationService.getDashboardTab();
        if (valueFromNotification) {
          context.vppaGridTabs.activeid = valueFromNotification;
          if (valueFromNotification === SELLER_DASHBOARD_GRID_TABS.TERMINATED_TAB) {
            context.allTerminatedVppa();
            //context.notificationService.clearDashboardTab();
          }
        }
      },
    };
    this.notifier.subscribe(this.handler, 'dashboardTab');
  }

  disconnectedCallback() {
    this.notifier.unsubscribe(this.handler);
    super.disconnectedCallback();
  }

  public allTradingActivityVppa() {
    this.getCompanyId();
    this.isNegotiationTabSelected = false;
    this.allVppa.vppaRfqCriteria = {
      vppaCriteria: `NEGOTIATION_ID != null && (VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' ||  VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') && NEGOTIATION_STATUS != '${NEGOTIATION_STATUS.REJECTED}'`,
      rfqNegotiationCriteria: `${this.allVppa.defaultRfqCriteria()}`,
    };
    this.allVppa.setInternalReferenceVisibility(true);
  }

  public allActiveVppa() {
    this.getCompanyId();
    this.isNegotiationTabSelected = false;
    this.allVppa.vppaRfqCriteria = {
      vppaCriteria: `((VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}') ||
      (CONTRACT_TYPE == '${CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}' && VPPA_STATUS == '${VPPA_RFQ_STATUS.AWAITING_KEY_METRICS}')) && NEGOTIATION_STATUS != '${NEGOTIATION_STATUS.REJECTED}'`,
      rfqNegotiationCriteria: `SELLER_COMPANY_ID=='${this.companyId}' && SELLER_VISIBILITY == true  && (RFQ_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') &&
      (NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.IN_PROGRESS}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REQUIRES_CONFIRMATION}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_BUYER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_SELLER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.SUCCESSFUL}') && NEGOTIATION_STATUS != '${NEGOTIATION_STATUS.REJECTED}'`,
    };

    this.allVppa.setInternalReferenceVisibility(true);
  }

  public allInactiveVppa() {
    this.getCompanyId();
    this.isNegotiationTabSelected = false;
    this.allVppa.vppaRfqCriteria = {
      vppaCriteria: `( (VPPA_STATUS == '${VPPA_RFQ_STATUS.DELETED}') || (VPPA_STATUS == '${VPPA_RFQ_STATUS.DELISTED}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.INSUFFICIENT_DATA}') || 
      (CONTRACT_TYPE != '${CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}' && VPPA_STATUS == '${VPPA_RFQ_STATUS.AWAITING_KEY_METRICS}'))`,
      rfqNegotiationCriteria: `SELLER_COMPANY_ID=='${this.companyId}' && SELLER_VISIBILITY == true`,
    };

    this.allVppa.setInternalReferenceVisibility(true);
  }

  public allExecutedVppa() {
    this.getCompanyId();
    this.isNegotiationTabSelected = true;
    this.negotiationGrid.matchCriteria = `(NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED}')`;
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.SELLER_COMPANY_NAME,
      false,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.BUYER_COMPANY_NAME,
      true,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.EXECUTED_DATE_TIME,
      true,
    );
  }

  public allTerminatedVppa() {
    this.getCompanyId();
    this.isNegotiationTabSelected = true;
    this.negotiationGrid.matchCriteria = `(NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REJECTED}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.TRADED_AWAY}')`;
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.SELLER_COMPANY_NAME,
      false,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.BUYER_COMPANY_NAME,
      true,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.EXECUTED_DATE_TIME,
      false,
    );
  }

  public allVppaNegotiations() {
    this.getCompanyId();
    this.isNegotiationTabSelected = true;
    this.negotiationGrid.matchCriteria =
      `(NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.IN_PROGRESS_ARTERIA}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.PENDING_EXECUTION}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.PENDING_EXECUTION_TIMER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.READY_FOR_TRADE_EXECUTION}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REVIEW_EXECUTION_CHECKLIST}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED_AWAITING_BUYER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED_AWAITING_SELLER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXPIRED}')`;
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.SELLER_COMPANY_NAME,
      false,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.BUYER_COMPANY_NAME,
      true,
    );
    this.negotiationGrid.vppaGrid.columnApi.setColumnVisible(
      COLUMN_FIELDS.EXECUTED_DATE_TIME,
      false,
    );
  }

  public openVppaModal() {
    this.$emit('openModal');
  }

  public projectChanged(oldValue, newValue: Project) {
    if (this.projectId == newValue.PROJECT_ID) {
      return;
    }

    this.projectId = newValue.PROJECT_ID;

    switch (this.vppaGridTabs.activeid) {
      case SELLER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB:
        this.allTradingActivityVppa();
        break;
      case SELLER_DASHBOARD_GRID_TABS.ACTIVE_CONTRACTS_TAB:
        this.allActiveVppa();
        break;
      case SELLER_DASHBOARD_GRID_TABS.INACTIVE_CONTRACTS_TAB:
        this.allInactiveVppa();
        break;
      case SELLER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB:
        this.allVppaNegotiations();
        break;
      case SELLER_DASHBOARD_GRID_TABS.EXECUTED_TAB:
        this.allExecutedVppa();
        break;
      case SELLER_DASHBOARD_GRID_TABS.TERMINATED_TAB:
        this.allTerminatedVppa();
        break;
      default:
        break;
    }
  }

  private getCompanyId() {
    this.companyId = this.companyId
      ? this.companyId
      : this.companyService.getCurrentCompany().CASCADE_COMPANY_ID;
  }
  public showOnlyNegotiations(): boolean {
    return hasLegalOrAuthorizedSignatoryVisibility(this.userService.getFilteredPermissions());
  }

  public downloadGridCsv() {
    if (this.isNegotiationTabSelected) {
      const csvParams = {
        fileName: this.vppaGridTabs.activeid,
        processCellCallback: (params) => {
          let formattedValue = params.value;
          if (params.column.getColId() === COLUMN_FIELDS.FIXED_PRICE) {
            formattedValue = getFormattedNumber(params.value, '', 2, '$');
          } else if (params.column.getColId() === COLUMN_FIELDS.TENOR_START_DATE) {
            formattedValue = getFormattedDate(params.value);
          } else if (params.column.getColId() === COLUMN_FIELDS.EXECUTED_DATE_TIME) {
            formattedValue = convertDateToUTC(new Date(params.value)).toLocaleString();
          } else if (params.column.getColId() === COLUMN_FIELDS.SELLER_SETTLEMENT_POINT) {
            formattedValue = params.value
              ? params.node.data.SETTLEMENT_POINT
              : getGroupColumnContractType(params, COLUMN_FIELDS.RFQ_DETAILS).VPPA_SETTLEMENT_POINT;
          } else if (params.column.getColId() === COLUMN_FIELDS.BUYER_POST_COD_COLLATERAL) {
            formattedValue = getNumericPattern(
                params.value,
                getAbbreviationForCreditProfile(
                    getGroupColumnContractType(params, COLUMN_FIELDS.RFQ_DETAILS).RFQ_CREDIT_PROFILE
                 ),
                '$'
            );
          } else if (params.column.getColId() === COLUMN_FIELDS.SELLER_POST_COD_COLLATERAL) {
            formattedValue = getNumericPattern(params.value,
                getAbbreviationForCreditProfile(
                 getGroupColumnContractType(params, COLUMN_FIELDS.VPPA_DETAILS).VPPA_CREDIT_PROFILE),
                 '$');
          }
          return formattedValue;
        },
      };
      this.negotiationGrid.vppaGrid.gridApi.exportDataAsCsv(csvParams);
    } else {
      this.allVppa.downloadGridCsv();
    }
  }
}
